// @ts-strict-ignore
import type { SchemaInstance, WorkflowProps } from 'flow/store/engine/types';
import type { WorkflowState } from '../state';
import type {
  Snapshot,
  WorkflowHistoryEntry,
  WorkflowStateSnapshot,
  WorkflowSnapshots,
} from '../types';
import {
  canRedo,
  createSnapshot,
  isUndo,
  removeRedoStack,
  trimSubsequentSnapshots,
} from 'flow/service/history';
import type { HistoryAction } from 'flow/service/history/constants';
import * as jsondiffpatch from 'jsondiffpatch';

// TODO: delete legacy undo/redo reducers when undo/redo is released

export function clearWorkflowSnapshots(
  state: WorkflowState,
) {
  const currentWorkflowData = state.workflowData;
  const currentWorkflowProps = state.workflowProps;
  state.workflowSnapshots.workflowData.history.initial = createSnapshot({
    data: currentWorkflowData,
  });
  state.workflowSnapshots.workflowData.history.past = [];
  state.workflowSnapshots.workflowProps.history.initial = createSnapshot({
    data: currentWorkflowProps,
  });
}

export function updateWorkflowSnapshots(
  state: WorkflowState,
  action: {
    payload: {
      workflowSnapshots: WorkflowSnapshots;
    };
  },
) {
  const { workflowSnapshots } = action.payload;
  state.workflowSnapshots = workflowSnapshots;
}

export function applyWorkflowDataSnapshot(
  state: WorkflowState,
  action: {
    payload: {
      snapshot: Snapshot<Partial<SchemaInstance>>;
    };
  },
) {
  state.workflowData = action.payload.snapshot.data;
}

export function applyWorkflowPropsSnapshot(
  state: WorkflowState,
  action: {
    payload: {
      snapshot: Snapshot<WorkflowProps>;
    };
  },
) {
  state.workflowProps = action.payload.snapshot.data;
}

/** Removes the snapshots from the array that come after the snapshot identified by snapshotId. */
export function removeSubsequentWorkflowDataSnapshots(
  state: WorkflowState,
  action: {
    payload: {
      snapshotId: Snapshot<Partial<SchemaInstance>>['snapshotId'];
    };
  },
) {
  const { snapshotId } = action.payload;
  if (state.workflowSnapshots.workflowData.history.initial.snapshotId === snapshotId) {
    state.workflowSnapshots.workflowData.history.past = [];
    return;
  }

  const snapshots = state.workflowSnapshots.workflowData.history.past;
  state.workflowSnapshots.workflowData.history.past = (
    trimSubsequentSnapshots(snapshots, snapshotId)
  );
}

// ================ Workflow History Reducers ================

export function setPendingWorkflowHistoryTransaction(
  state: WorkflowState,
  action: {
    payload: {
      beforeState: WorkflowStateSnapshot;
    };
  },
) {
  state.editHistory.pendingBeforeState = action.payload.beforeState;
}

export function clearPendingWorkflowHistoryTransaction(
  state: WorkflowState,
) {
  state.editHistory.pendingBeforeState = null;
}

/**
 * Appends a new workflow history entry to the history array.
 * Slices out the "redo" stack if there is a "redo" stack present.
 */
export function appendWorkflowHistoryEntry(
  state: WorkflowState,
  action: {
    payload: {
      entry: WorkflowHistoryEntry;
    };
  },
) {
  const { history, historyIndex } = state.editHistory;

  if (canRedo(history, historyIndex)) {
    state.editHistory.history = removeRedoStack(history, historyIndex);
  }

  state.editHistory.history.push(action.payload.entry);
  state.editHistory.historyIndex = state.editHistory.history.length - 1;
}

// assume this will only be called on well-formed arguments
export function applyHistoryTraversal(
  state: WorkflowState,
  action: {
    payload: {
      historyAction: HistoryAction;
    };
  },
) {
  const { historyAction } = action.payload;

  const entryIndex = isUndo(historyAction)
    ? state.editHistory.historyIndex
    : state.editHistory.historyIndex + 1;

  const {
    diff: {
      workflowData: workflowDataDiff,
      workflowProps: workflowPropsDiff,
    },
  } = state.editHistory.history[entryIndex];

  const applyDiff = isUndo(historyAction) ? jsondiffpatch.unpatch : jsondiffpatch.patch;

  applyDiff(state.workflowData, workflowDataDiff);
  if (workflowPropsDiff) {
    applyDiff(state.workflowProps, workflowPropsDiff);
  }

  state.editHistory.historyIndex += isUndo(historyAction) ? -1 : 1;
}
